import { Observable, of, tap } from 'rxjs';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

/**
 * Returns cached data immediately if available and within TTL, then
 * revalidates in the background on the next call after TTL expires.
 *
 * If there is no cached entry, fetches from the source and caches the result.
 */
export function cachedLoad<T>(config: {
  key: string;
  fetcher: () => Observable<T>;
  ttlMs?: number;
}): Observable<T> {
  const ttl = config.ttlMs ?? 5 * 60 * 1000;
  const entry = cache.get(config.key) as CacheEntry<T> | undefined;

  if (entry && Date.now() - entry.timestamp < ttl) {
    return of(entry.data);
  }

  return config
    .fetcher()
    .pipe(tap((data) => cache.set(config.key, { data, timestamp: Date.now() })));
}

export function invalidateCache(key: string): void {
  cache.delete(key);
}

export function clearAllCache(): void {
  cache.clear();
}
